"use client";

import * as React from "react";
import { usePrefersReducedMotion } from "../../hooks/use-prefers-reduced-motion";
import { Box } from "../../components/ui/box";
import { Text } from "../../components/ui/text";
import type { Omittable } from "../../lib/props";

/**
 * Map marker glyphs — the teardrop pin, the cluster bubble and the infobox
 * caret, drawn as SVG so their fills can be driven by runtime colors (map
 * palettes are resolved values, not tokens: they paint over imagery the CSS
 * theme does not own).
 *
 * Entity-agnostic: the consumer decides what a pin's color, count or overlay
 * icon means.
 */

/** Enter duration in ms; matches the native marker-birth spring. */
export const MAP_MARKER_ENTER_MS = 380;
export const MAP_MARKER_EXIT_MS = 150;
export const MAP_INFOBOX_ENTER_MS = 240;
export const MAP_INFOBOX_STAGGER_MS = 75;

const EASE_OUT = "cubic-bezier(0, 0, 0.2, 1)";
const SPRING_EASE = "cubic-bezier(0.16, 1.25, 0.3, 1)";
const INFOBOX_EASE = "cubic-bezier(0.16, 1, 0.3, 1)";
const ENTER_SCALE = 0.02;
const EXIT_SCALE = 0.6;
const INFOBOX_ENTER_SCALE = 0.92;
/** Selection lift in ms; matches the native `Motion.fast` spring. */
export const MAP_PIN_SELECT_MS = 100;
const SELECTED_SCALE = 1.2;

function usePaintedPortalReveal(): boolean {
  const [revealed, setRevealed] = React.useState(false);
  React.useEffect(() => {
    let revealFrame: number | null = null;
    const paintFrame = requestAnimationFrame(() => {
      revealFrame = requestAnimationFrame(() => setRevealed(true));
    });
    return () => {
      cancelAnimationFrame(paintFrame);
      if (revealFrame !== null) cancelAnimationFrame(revealFrame);
    };
  }, []);
  return revealed;
}

export type MapMarkerTransitionProps = Omittable<{
  /**
   * When true the glyph animates out (fades + shrinks). It must stay mounted
   * for at least `MAP_MARKER_EXIT_MS` — a Google Maps `AdvancedMarker` removes
   * its portal DOM the instant the marker unmounts, so a React-unmount-driven
   * exit can never play. Drive it from a presence controller instead.
   */
  exiting?: boolean;
  /** `bottom` keeps the pin's tip planted while it scales. */
  origin?: "center" | "bottom";
  children: React.ReactNode;
}>;

/**
 * Prop-driven enter/exit for a marker. Pure CSS transitions: the from-state
 * (faded + shrunk) stays committed through one paint before the next frame
 * reveals it. The extra frame is load-bearing inside an AdvancedMarker portal,
 * where a same-frame reveal can finish before Google attaches visible content.
 * Honors OS reduce-motion.
 */
export function MapMarkerTransition({
  exiting = false,
  origin = "center",
  children,
}: MapMarkerTransitionProps): React.ReactElement {
  const reduceMotion = usePrefersReducedMotion();
  const entered = usePaintedPortalReveal();

  const shown = reduceMotion ? !exiting : entered && !exiting;
  const duration = exiting ? MAP_MARKER_EXIT_MS : MAP_MARKER_ENTER_MS;
  const hiddenScale = exiting ? EXIT_SCALE : ENTER_SCALE;

  return (
    <Box
      data-slot="map-marker-transition"
      style={{
        opacity: shown ? 1 : 0,
        transform: shown ? "scale(1)" : `scale(${hiddenScale})`,
        transformOrigin: origin === "bottom" ? "center bottom" : "center",
        transitionProperty: reduceMotion ? undefined : "opacity, transform",
        transitionDuration: reduceMotion ? undefined : `${duration}ms`,
        transitionTimingFunction: reduceMotion
          ? undefined
          : exiting
            ? EASE_OUT
            : `${EASE_OUT}, ${SPRING_EASE}`,
      }}
    >
      {children}
    </Box>
  );
}

export type MapInfoboxTransitionProps = Omittable<{
  /** Start shortly after a concurrently-mounted marker entrance. */
  staggerWithMarker?: boolean;
  children: React.ReactNode;
}>;

/**
 * Mount transition for a map infobox and its caret. The bottom-center origin
 * keeps the caret tip attached to the marker while the card grows upward.
 * The reveal waits through one paint so an AdvancedMarker portal cannot skip
 * the starting state. Honors OS reduce-motion.
 */
export function MapInfoboxTransition({
  staggerWithMarker = false,
  children,
}: MapInfoboxTransitionProps): React.ReactElement {
  const reduceMotion = usePrefersReducedMotion();
  const revealed = usePaintedPortalReveal();
  const shown = reduceMotion || revealed;

  return (
    <Box
      data-slot="map-infobox-transition"
      style={{
        opacity: shown ? 1 : 0,
        transform: shown
          ? "translateY(0) scale(1)"
          : `translateY(6px) scale(${INFOBOX_ENTER_SCALE})`,
        transformOrigin: "center bottom",
        transitionProperty: reduceMotion ? undefined : "opacity, transform",
        transitionDuration: reduceMotion
          ? undefined
          : `${MAP_INFOBOX_ENTER_MS}ms`,
        transitionDelay:
          reduceMotion || !staggerWithMarker
            ? undefined
            : `${MAP_INFOBOX_STAGGER_MS}ms`,
        transitionTimingFunction: reduceMotion
          ? undefined
          : `${EASE_OUT}, ${INFOBOX_EASE}`,
      }}
    >
      {children}
    </Box>
  );
}

// Teardrop geometry, authored on a 41×51 canvas. The body path and the 1px
// outset outline path are separate so the stroke can carry its own color.
const PIN_BODY =
  "M20.0769 2C30.0605 2 38.1538 10.0933 38.1538 20.0769C38.1538 26.0213 35.2842 31.2949 30.8551 34.5897C27.4485 37.1498 22.5822 41.2065 21.4706 47.7696C21.3539 48.4591 20.7763 48.9947 20.0769 48.9947C19.3776 48.9947 18.8 48.4591 18.6832 47.7696C17.5716 41.2061 12.7045 37.1497 9.29785 34.5897C4.8691 31.2949 2 26.021 2 20.0769C2 10.0933 10.0933 2 20.0769 2Z";
const PIN_OUTLINE =
  "M20.0771 1C30.6129 1.00012 39.1543 9.54133 39.1543 20.0771C39.1542 26.3492 36.1244 31.9123 31.4551 35.3877L31.4561 35.3887C28.0599 37.9409 23.4977 41.792 22.457 47.9365C22.267 49.0589 21.3115 49.995 20.0771 49.9951C18.8427 49.9951 17.8873 49.0589 17.6973 47.9365C16.6566 41.7916 12.0936 37.941 8.69727 35.3887V35.3877C4.0284 31.9122 1.00007 26.3487 1 20.0771C1 9.54125 9.54125 1 20.0771 1Z";

export const MAP_PIN_WIDTH = 41;
export const MAP_PIN_HEIGHT = 51;

export type MapPinGlyphProps = Omittable<{
  /** Teardrop body fill — a concrete paintable color, not a token. */
  color: string;
  /** Outline stroke color. */
  outlineColor: string;
  /** Fill of the inner disc the label/overlay sits on. */
  overlayColor: string;
  /** Text drawn on the disc (a count). Omit when `children` supplies a glyph. */
  label?: string;
  labelColor?: string;
  labelFontSize?: number;
  /** Overlay slot centered on the disc — e.g. an `<Icon/>`. */
  children?: React.ReactNode;
  exiting?: boolean;
  /** Lifts the pin (scaled up from its tip, thicker outline). */
  selected?: boolean;
  /** What the pin marks, read by a screen reader and never drawn. A
   *  clickable map marker takes its name from this text. */
  accessibleLabel?: string;
}>;

export function MapPinGlyph({
  color,
  outlineColor,
  overlayColor,
  label,
  labelColor,
  labelFontSize = 14,
  children,
  exiting,
  selected = false,
  accessibleLabel,
}: MapPinGlyphProps): React.ReactElement {
  const reduceMotion = usePrefersReducedMotion();
  return (
    <MapMarkerTransition exiting={exiting} origin="bottom">
      {/* The overlay slot is absolutely positioned, so the frame is explicitly
        * relative — a plain Box is `position: static`. The selection scale
        * lives here, inside the enter/exit shell, so the two transforms
        * compose instead of overwriting each other. The transition also
        * covers opacity, so a consumer stylesheet can fade the pin. */}
      <Box
        cursor="pointer"
        data-selected={selected || undefined}
        data-slot="map-pin-glyph"
        height={MAP_PIN_HEIGHT}
        position="relative"
        style={{
          transform: selected ? `scale(${SELECTED_SCALE})` : undefined,
          transformOrigin: "center bottom",
          transition: reduceMotion
            ? undefined
            : `transform ${MAP_PIN_SELECT_MS}ms ${EASE_OUT}, opacity ${MAP_PIN_SELECT_MS}ms ${EASE_OUT}`,
        }}
        width={MAP_PIN_WIDTH}
      >
        {accessibleLabel ? <Text visuallyHidden>{accessibleLabel}</Text> : null}
        {/* The selected outline outgrows the viewBox edge it is drawn on. */}
        <svg
          aria-hidden
          height={MAP_PIN_HEIGHT}
          overflow={selected ? "visible" : undefined}
          viewBox={`0 0 ${MAP_PIN_WIDTH} ${MAP_PIN_HEIGHT}`}
          width={MAP_PIN_WIDTH}
        >
          <path d={PIN_BODY} fill={color} />
          <path
            d={PIN_OUTLINE}
            fill="none"
            stroke={outlineColor}
            strokeWidth={selected ? 3 : 2}
          />
          <circle cx={20} cy={20} fill={overlayColor} r={15} />
          {label != null && (
            <text
              fill={labelColor}
              fontSize={labelFontSize}
              fontWeight="600"
              textAnchor="middle"
              x={20}
              y={25}
            >
              {label}
            </text>
          )}
        </svg>
        {children != null && (
          // Centered on the disc (cx=20, cy=20, r=15).
          <Box
            align="center"
            height={30}
            justify="center"
            left={5}
            position="absolute"
            top={5}
            width={30}
          >
            {children}
          </Box>
        )}
      </Box>
    </MapMarkerTransition>
  );
}

export type MapClusterGlyphProps = Omittable<{
  /** Rendered edge length in px; the geometry is authored on a 30×30 canvas. */
  size: number;
  count: number;
  color: string;
  outlineColor: string;
  textColor: string;
  fontSize: number;
  strokeWidth: number;
  exiting?: boolean;
}>;

export function MapClusterGlyph({
  size,
  count,
  color,
  outlineColor,
  textColor,
  fontSize,
  strokeWidth,
  exiting,
}: MapClusterGlyphProps): React.ReactElement {
  return (
    <MapMarkerTransition exiting={exiting}>
      <Box cursor="pointer" data-slot="map-cluster-glyph">
        <svg aria-hidden height={size} viewBox="0 0 30 30" width={size}>
          <circle
            cx={15}
            cy={15}
            fill={color}
            r={14}
            stroke={outlineColor}
            strokeWidth={strokeWidth}
          />
          <text
            dominantBaseline="middle"
            fill={textColor}
            fontSize={fontSize}
            fontWeight="600"
            textAnchor="middle"
            x="50%"
            y="50%"
          >
            {count}
          </text>
        </svg>
      </Box>
    </MapMarkerTransition>
  );
}

/**
 * The downward caret under a map infobox. Paints with the `popover` tokens
 * (unlike the marker glyphs it sits on the popup, not on map imagery) and
 * continues the card's frame: a hairline-colored triangle underneath, the
 * popover fill inset one hairline on its two diagonal edges. It overlaps the
 * card by that hairline so the fill covers the card's bottom border where the
 * caret attaches — otherwise a line runs across the caret's base.
 */
export function MapInfoboxCaret(): React.ReactElement {
  return (
    <Box align="center" className="-mt-px" data-slot="map-infobox-caret">
      <svg aria-hidden height={12} viewBox="0 0 24 12" width={24}>
        <path d="M0 0 L12 12 L24 0 Z" fill="var(--popover-border)" />
        {/* Diagonal edges run at 45°, so a 1px perpendicular inset is √2 along
            each axis. */}
        <path
          d="M1.414 0 L12 10.586 L22.586 0 Z"
          fill="var(--popover)"
        />
      </svg>
    </Box>
  );
}
